import React, { memo, useState, useEffect, useCallback, useRef } from 'react'
import { SlickGridRef } from '../..'
import SlickGridComponent from '../../index'
import { useSetState } from 'ahooks'
import { Column, GridOption, SlickgridReactInstance } from 'slickgrid-react'
import { Button, message } from 'antd'
import { CopyOutlined, UpOutlined } from '@ant-design/icons'
import { treeFormatter } from '@/utils/slickgrid'
import { isFirstChild } from '@/utils/tree'
import { isUndefined } from '@/utils/tools'
import { dataSource2 } from './utils'
import { useDebounceFn } from 'ahooks'
import styles from './index.module.less'

// 为Window添加_latestGridData属性
declare global {
  interface Window {
    _latestGridData?: any[];
  }
}

interface IndicatorItem {
  id: number | string;
  name: string;
  pid?: number | string;
  hasChildren?: boolean;
  children?: IndicatorItem[];
  __hasChildren?: boolean;
  __collapsed?: boolean;
  __tree_level?: number;
  isQd?: boolean;
  childCount?: number;
  loaded?: boolean;
}

interface CustomTreeToggleItem {
  dataContext: IndicatorItem;
  item: IndicatorItem;
  grid: any;
}

interface TreeToggleDetail {
  type: 'toggle-expand' | 'toggle-collapse';
  previousFullToggleType: string;
  toggledItems: Array<{
    itemId: number | string;
    isCollapsed: boolean;
  }>;
  fromItemId: number | string;
}

const Index = (props: any) => {
  const [state, setState] = useSetState({
    loading: false,
    height: 400
  })

  useEffect(() => {
    setState({ loading: false });
  }, []);

  // 创建占位节点的函数
  const createPlaceholders = (parentId: string | number, count: number, item: any): IndicatorItem[] => {
    // 获取父节点的层级，如果没有则默认为0
    const parentLevel = item.__tree_level || 0;

    return Array.from({ length: count }).map((_, idx) => ({
      id: `${parentId}_placeholder_${idx}`,
      name: '加载中...',
      pid: parentId,
      // "__collapsed": false,
      // __hasChildren: false,
      // __treeLevel: parentLevel + 1,  // 正确设置层级为父节点+1
    }));
  };

  // 简化版：初始化树数据，直接为有childCount的节点生成占位节点
  const initializeTreeData = (data: IndicatorItem[]): IndicatorItem[] => {
    return data.map(item => {
      // 深拷贝当前项，避免直接修改原数据
      const newItem = { ...item };

      // 如果设置了childCount且没有children，直接生成占位节点
      if (newItem.childCount && (!newItem.children || newItem.children.length === 0)) {
        newItem.children = createPlaceholders(newItem.id, newItem.childCount, newItem);
        // newItem.__hasChildren = true;
      }
      // 如果有子节点，递归处理
      else if (newItem.children && newItem.children.length > 0) {
        newItem.children = initializeTreeData(newItem.children);
      }

      return newItem;
    });
  };

  // 模拟异步加载数据，根据count参数动态生成不同数量的子节点
  const loadChildrenData = async (parentId: string | number, count: number = 20): Promise<IndicatorItem[]> => {
    console.log("parentId", parentId, "count", count);
    // 模拟API调用延迟
    await new Promise(resolve => setTimeout(resolve, 1000));

    // 动态生成指定数量的子节点
    return Array.from({ length: count }).map((_, idx) => ({
      id: `${parentId}_child_${idx + 1}`,
      name: `${parentId}的子节点${idx + 1}`,
      pid: parentId,
      "__collapsed": false
      // 为部分节点添加可再展开的属性，使树形结构更丰富
    }));
  };

  const handleTreeItemToggled = async (e: CustomEvent<TreeToggleDetail>) => {
    console.log('handleTreeItemToggled', e.detail);
    const { type, toggledItems, fromItemId } = e.detail;

    return;
    // 只处理展开操作
    if (type === 'toggle-expand') {
      const gridInstance = gridRef.current?.getReactGridInstance();
      const dataView = gridInstance?.dataView;
      const slickGrid = gridInstance?.slickGrid;
      if (!dataView || !slickGrid) return;

      // 获取被展开的节点
      const item = dataView.getItemById(fromItemId);
      if (!item) return;

      // 只有标记了 isQd: true 的节点，且子节点都是占位节点，才进行动态加载
      if (item.isQd && !item.loaded && item.childCount) {
        try {
          // setState({ loading: true });

          // 获取子节点数量（从已有的占位节点数量或childCount属性）
          const childCount = item.childCount || 20;

          // 获取真实数据
          const realChildren = await loadChildrenData(fromItemId, childCount);

          // 更新父节点状态
          const updatedItem = { ...item, loaded: true };
          dataView.updateItem(fromItemId, updatedItem);

          // 逐个更新占位节点为真实数据
          realChildren.forEach((child, idx) => {
            const placeholderId = `${fromItemId}_placeholder_${idx}`;
            const updatedChild = {
              ...child,
              pid: fromItemId,
              __hasChildren: false,
              __treeLevel: item.__tree_level ? item.__tree_level + 1 : 1,
              __collapsed: false
            };
            dataView.updateItem(placeholderId, updatedChild);
          });

          // 确保数据完全更新
          dataView.refresh();
          slickGrid.invalidate();
          slickGrid.render();

          // 同步更新React状态以便handleGetTable能获取到正确数据
          const allItems = dataView.getItems();
          // 这里不直接设置dataSource，因为那会导致整个组件重新渲染
          // 而是记录更新后的数据，以便handleGetTable使用
          window._latestGridData = allItems;
        } catch (error) {
          message.error('加载数据失败');
          console.error(error);
        } finally {
          // setState({ loading: false });
        }
      }
    }
  }

  const gridOptions: GridOption = {
    gridWidth: '100%',
    autoEdit: true,
    autoCommitEdit: true,
    enableFiltering: true,
    enableTreeData: true,
    multiColumnSort: false,
    autoResize: {
      container: '#indicator_zjzb_table_container',
      rightPadding: 0,
      bottomPadding: 0,
      calculateAvailableSizeBy: 'container'
    },
    enableAutoResize: true,
    treeDataOptions: {
      identifierPropName: 'id',
      parentPropName: 'pid',
      columnId: 'name', // 指定树状数据的列ID
      childrenPropName: 'children', // 指定子项属性名称
      autoApproveParentItemWhenTreeColumnIsValid: true,
      autoRecalcTotalsOnFilterChange: false,
      excludeChildrenWhenFilteringTree: false,
      initialSort: { columnId: 'sort', direction: 'asc' }
    },
    datasetIdPropertyName: 'id',
    // 使用类型断言来绕过 TypeScript 错误
    // 移除 rowCssClasses，因为它不在 GridOption 类型中
  }

  const gridRef = useRef<SlickGridRef>(null)

  const columns: Column[] = [
    {
      id: 'id',
      name: '',
      field: 'id',
      minWidth: 60,
      maxWidth: 70,
      cssClass: 'slickgrid_column_number slickgrid-checkbox-center-cls',
      behavior: "select",
      sortable: true,
    },
    {
      id: 'name',
      field: 'name',
      name: '指标科目',
      cssClass: 'slickgrid-edit',
      sortable: true,
      formatter: treeFormatter
    },
    {
      id: 'id1',
      name: '',
      field: 'id1',
      behavior: "select",
      sortable: true,
    },
    {
      id: 'id2',
      name: '',
      field: 'id2',
      behavior: "select",
      sortable: true,
    },
  ]

  // 树形数据 - 修改为有childCount的节点
  const dataSourceData: any[] = [
    {
      id: 1,
      name: '一级指标科目',
      hasChildren: true,
      tree_level: 0,
      children: [
        {
          id: 5,
          pid: 1,
          isQd: true,
          name: '5级指标科目1',
          childCount: 20, // 预设子节点数量
          tree_level: 1,
          __collapsed: true,
        },
        {
          id: 4,
          pid: 1,
          name: '5级指标科目2',
          __collapsed: true,
          isQd: true,
          tree_level: 1,
          childCount: 50, // 预设大量子节点测试性能
        },
        {
          id: 6,
          pid: 1,
          name: '普通节点',
          __collapsed: true,
          tree_level: 1,
          children: [

            { id: 61, pid: 6, name: '普通子节点1', tree_level: 2 },
            { id: 62, pid: 6, name: '普通子节点2', tree_level: 2 },
          ]
        },
        {
          id: 7,
          pid: 1,
          name: '普通节点2',
          __collapsed: true,
          tree_level: 1,
          children: [

            { id: 611, pid: 7, name: '普通子节点21', tree_level: 2 },
            { id: 622, pid: 7, name: '普通子节点22', tree_level: 2 },
          ]
        }
      ]
    }
  ];

  // 初始化数据，为需要异步加载的节点创建占位节点
  const [dataSource, setDataSource] = useState<IndicatorItem[]>(
    // initializeTreeData(dataSourceData)
    dataSourceData
  );

  console.log("dataSource", dataSource)

  const getReactGridInstance = () => gridRef.current?.getReactGridInstance()

  const handleClick = () => {
    setState({
      height: 500
    })
    getReactGridInstance()?.resizerService.resizeGrid(0, { height: state.height, width: '100%' });
  }

  const handleCopy = () => {
    const rows = getReactGridInstance().gridService.getSelectedRows()
    if (rows.length) {
      const rowItem = getReactGridInstance().gridService.getDataItemByRowIndex(rows[0])
      if (!rowItem) return;
      if (rowItem.level == 1) {
        return;
      }
      const targetKm = {
        ...rowItem,
        name: rowItem.name + '1',
        code: rowItem.code + '1',
        sort: rowItem.sort + 1,
        id: new Date().getTime()
      }

      getReactGridInstance().dataView.insertItem(rows[0] + 1, targetKm)
    } else {
      message.warning("请选中要复制的行序号")
    }
  }

  const handleUp = () => {
    if (!getReactGridInstance()?.dataView) return;
    const selectedRows = getReactGridInstance()?.slickGrid.getSelectedRows();
    const curRow = selectedRows[0]
    if (isUndefined(curRow)) {
      message.warning("请选择要移动的行")
      return;
    }
    let item = getReactGridInstance()?.dataView.getItem(curRow);
    if (item.pid) {
      if (isFirstChild(dataSource, item, { id: 'id', parentId: 'pid' })) {
        message.warning("已经是第一个节点了")
        return
      }
    }
    if (curRow > 0) {
      let targetRow = curRow - 1
      let targetSort = item.serial - 1
      const moveRows = [{ ...item, serial: targetSort }]

      getReactGridInstance().slickGrid.setActiveRow(targetRow)
      getReactGridInstance().gridService.deleteItems(moveRows)
      getReactGridInstance().dataView.insertItems(targetRow, moveRows)
    } else {
      message.warning("已经到顶了")
    }
  }
  const handleDown = () => {

  }

  const handleInsert = () => {
    if (!getReactGridInstance()?.dataView) return;
    const dataView = getReactGridInstance()?.dataView

    const selectedRows = getReactGridInstance()?.slickGrid.getSelectedRows();
    if (selectedRows.length) {
      const curRow = selectedRows[0]
      if (curRow > -1) {
        const item = getReactGridInstance()?.dataView.getItem(curRow);
        console.log("item", item)
        const newItem = { pid: item.id, __parentId: item.id, id: new Date().getTime() + '', name: '插入节点' }
        console.log("newItem", newItem)
        dataView.insertItem(curRow + 1, newItem)
        dataView.refresh()
      }
    }
  }

  const handleGetTable = () => {
    // 优先使用_latestGridData，如果有的话
    const data = window._latestGridData || getReactGridInstance()?.dataView.getItems()
    console.log("handleGetTable", data)
  }

  const handleSetTable = () => {
    // setDataSource(dataSource2)
  }

  // useEffect(() => {
  //   const gridInstance = gridRef.current?.getReactGridInstance();
  //   if (gridInstance) {
  //     setTableCellStyle(gridInstance);
  //   }
  // }, []);

  // 设置单元格样式
  const setTableCellStyle = () => {
    console.log("setTableCellStyle")
    const reactGrid = gridRef.current?.getReactGridInstance();
    if (!dataSource.length) return;
    const items = reactGrid.dataView.getItems();
    // 获取当前可见的项
    const visibleItems = reactGrid.dataView.getFilteredItems();
    console.log("visibleItems", visibleItems)
    if (!visibleItems.length) return;
    let cssStyleHash = {};
    console.log("items", visibleItems)
    visibleItems.forEach((item, rowIndex) => {
      const level = item.tree_level || 0;
      console.log("level", item.name, level, item)
      // 只给 name 列加色，className 必须和 less 保持一致
      cssStyleHash[rowIndex] = { name: `level-${level}` };
    });
    reactGrid.slickGrid.setCellCssStyles("TreeLevelColor", cssStyleHash);
  };



  const { run: debouncedSetTableCellStyle } = useDebounceFn(
    () => {
      setTableCellStyle(); // 你的跳转逻辑
    },
    {
      wait: 100, // 500ms 内只执行一次
      leading: false, // 是否立即执行
      trailing: true, // 最后一次调用是否执行
    }
  );


  const handleReactGridCreated = (e: CustomEvent<any>) => {
    const reactGrid: SlickgridReactInstance = e.detail;
    debouncedSetTableCellStyle()
  };

  const handleSetTableColor = () => {
    const gridInstance = getReactGridInstance();
    if (gridInstance) {
      debouncedSetTableCellStyle();
    } else {
      message.warning('表格未初始化');
    }
  }

  const handleRouwCountChanged = (e: CustomEvent<any>) => {
    const { rowCount } = e.detail;
    // setRowCount(rowCount);
    debouncedSetTableCellStyle();
  };

  return (<div>
    <div className='flex'>
      <Button onClick={handleCopy} className="c1f1f1f" type='link' size='small' icon={<CopyOutlined />}>复制节点并插入</Button>
      <Button className="c1f1f1f1" type='link' onClick={handleUp} size='small' >上移</Button>
      <Button className="c1f1f1f1" type='link' onClick={handleDown} size='small' >下移</Button>
      <Button className="c1f1f1f1" type='link' onClick={handleInsert} size='small' >插入子节点</Button>
      {/* 获取表格数据按钮 */}
      <Button className="c1f1f1f1" type='link' onClick={handleGetTable} size='small' >获取表格数据</Button>
      {/* 设置表格数据 */}
      <Button className="c1f1f1f1" type='link' onClick={handleSetTable} size='small' >设置表格数据</Button>
      {/* 设置表格颜色 */}
      <Button className="c1f1f1f1" type='link' onClick={handleSetTableColor} size='small' >设置表格颜色</Button>
    </div>
    <div className={styles['tableWrapper']} style={{ height: `${state.height}px` }} id='indicator_zjzb_table_container'>
      <SlickGridComponent
        loading={state.loading}
        ref={gridRef}
        gridId='indicator_zjzb_grid'
        gridOptions={gridOptions}
        columnDefinitions={columns}
        datasetHierarchical={dataSource}
        onRowCountChanged={handleRouwCountChanged}
        onTreeItemToggled={handleTreeItemToggled}
        onReactGridCreated={handleReactGridCreated}
      />
    </div>
  </div>)
}

export default Index
